import React, { Fragment, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition, Disclosure } from '@headlessui/react';
import { 
    XMarkIcon, 
    ChevronDownIcon,
    ChevronRightIcon,
    HomeIcon,
    HeartIcon,
    ShoppingBagIcon,
    UserIcon,
    MagnifyingGlassIcon,
    Bars3Icon
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useFavorites } from '../context/FavoritesContext';
import api from '../utils/api';

function MobileMenu() {
    const [isOpen, setIsOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(false);
    const { session, user, logout } = useAuth();
    const { cartItemCount } = useCart();
    const { favoritesCount } = useFavorites();

    // Kategorileri yükle
    useEffect(() => {
        if (isOpen && categories.length === 0) {
            fetchCategories();
        }
    }, [isOpen]);

    const fetchCategories = async () => {
        setLoading(true);
        try {
            const response = await api.get('/api/categories');
            setCategories(response.data || []);
        } catch (error) {
            console.error('Kategoriler yüklenirken hata:', error);
        } finally {
            setLoading(false);
        }
    };

    const closeMenu = () => setIsOpen(false);

    const handleLogout = async () => {
        try {
            await logout();
            closeMenu();
        } catch (error) {
            console.error('Çıkış hatası:', error);
        }
    };

    return (
        <>
            {/* Hamburger Menü Butonu */}
            <button
                onClick={() => setIsOpen(true)}
                className="lg:hidden p-2 text-gray-600 hover:text-brand-green"
            >
                <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Mobil Menü Overlay */}
            <Transition appear show={isOpen} as={Fragment}>
                <Dialog as="div" className="relative z-50 lg:hidden" onClose={closeMenu}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-25" />
                    </Transition.Child>

                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-start justify-start">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 -translate-x-full"
                                enterTo="opacity-100 translate-x-0"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-x-0"
                                leaveTo="opacity-0 -translate-x-full"
                            >
                                <Dialog.Panel className="w-full max-w-sm transform overflow-hidden bg-white shadow-xl transition-all h-full">
                                    <div className="flex flex-col h-full">
                                        {/* Header */}
                                        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-brand-green">
                                            <div className="flex items-center">
                                                <img 
                                                    src={process.env.PUBLIC_URL + '/minela_kids_logo.jpg'} 
                                                    alt="Minela Kids" 
                                                    className="h-8 w-auto rounded-md mr-3"
                                                />
                                                <span className="text-white font-bold text-lg">Menü</span>
                                            </div>
                                            <button
                                                onClick={closeMenu}
                                                className="p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-lg"
                                            >
                                                <XMarkIcon className="h-6 w-6" />
                                            </button>
                                        </div>

                                        {/* Kullanıcı Bilgileri */}
                                        <div className="p-4 bg-gray-50 border-b border-gray-200">
                                            {session ? (
                                                <div className="flex items-center">
                                                    <div className="w-10 h-10 bg-brand-green rounded-full flex items-center justify-center mr-3">
                                                        <UserIcon className="h-6 w-6 text-white" />
                                                    </div>
                                                    <div>
                                                        <p className="font-medium text-gray-900 truncate">
                                                            {user?.email}
                                                        </p>
                                                        <p className="text-sm text-gray-500">Hoş geldiniz!</p>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Link
                                                    to="/auth"
                                                    onClick={closeMenu}
                                                    className="flex items-center text-gray-700 hover:text-brand-green"
                                                >
                                                    <UserIcon className="h-6 w-6 mr-3" />
                                                    <span>Giriş Yap / Kayıt Ol</span>
                                                </Link>
                                            )}
                                        </div>

                                        {/* Hızlı Erişim */}
                                        <div className="p-4 border-b border-gray-200">
                                            <div className="grid grid-cols-3 gap-4">
                                                <Link
                                                    to="/"
                                                    onClick={closeMenu}
                                                    className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50"
                                                >
                                                    <HomeIcon className="h-6 w-6 text-brand-green mb-1" />
                                                    <span className="text-xs text-gray-600">Ana Sayfa</span>
                                                </Link>
                                                
                                                <Link
                                                    to="/favorites"
                                                    onClick={closeMenu}
                                                    className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50 relative"
                                                >
                                                    <HeartIcon className="h-6 w-6 text-brand-green mb-1" />
                                                    <span className="text-xs text-gray-600">Favoriler</span>
                                                    {favoritesCount > 0 && (
                                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                                            {favoritesCount}
                                                        </span>
                                                    )}
                                                </Link>
                                                
                                                <Link
                                                    to="/cart"
                                                    onClick={closeMenu}
                                                    className="flex flex-col items-center p-3 rounded-lg hover:bg-gray-50 relative"
                                                >
                                                    <ShoppingBagIcon className="h-6 w-6 text-brand-green mb-1" />
                                                    <span className="text-xs text-gray-600">Sepet</span>
                                                    {cartItemCount > 0 && (
                                                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                                            {cartItemCount}
                                                        </span>
                                                    )}
                                                </Link>
                                            </div>
                                        </div>

                                        {/* Ana Menü */}
                                        <div className="flex-1 overflow-y-auto">
                                            <nav className="p-4">
                                                {/* Arama */}
                                                <Link
                                                    to="/products"
                                                    onClick={closeMenu}
                                                    className="flex items-center p-3 rounded-lg hover:bg-gray-50 mb-4"
                                                >
                                                    <MagnifyingGlassIcon className="h-6 w-6 text-brand-green mr-3" />
                                                    <span className="font-medium text-gray-900">Ürün Ara</span>
                                                </Link>

                                                {/* Kategoriler */}
                                                <div className="space-y-2">
                                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                                        Kategoriler
                                                    </h3>
                                                    
                                                    {loading ? (
                                                        <div className="space-y-2">
                                                            {Array.from({ length: 4 }).map((_, i) => (
                                                                <div key={i} className="animate-pulse">
                                                                    <div className="h-12 bg-gray-200 rounded-lg"></div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        categories.map((category) => (
                                                            <CategoryMenuItem 
                                                                key={category.id} 
                                                                category={category} 
                                                                onClose={closeMenu}
                                                            />
                                                        ))
                                                    )}
                                                </div>

                                                {/* Diğer Sayfalar */}
                                                <div className="mt-8 pt-4 border-t border-gray-200">
                                                    <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                                        Sayfalar
                                                    </h3>
                                                    <div className="space-y-2">
                                                        <Link
                                                            to="/about"
                                                            onClick={closeMenu}
                                                            className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                        >
                                                            Hakkımızda
                                                        </Link>
                                                        <Link
                                                            to="/sss"
                                                            onClick={closeMenu}
                                                            className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                        >
                                                            Sık Sorulan Sorular
                                                        </Link>
                                                        <Link
                                                            to="/teslimat-bilgileri"
                                                            onClick={closeMenu}
                                                            className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                        >
                                                            Teslimat Bilgileri
                                                        </Link>
                                                        <Link
                                                            to="/iade-kosullari"
                                                            onClick={closeMenu}
                                                            className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                        >
                                                            İade Koşulları
                                                        </Link>
                                                    </div>
                                                </div>

                                                {/* Kullanıcı İşlemleri */}
                                                {session && (
                                                    <div className="mt-8 pt-4 border-t border-gray-200">
                                                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                                                            Hesabım
                                                        </h3>
                                                        <div className="space-y-2">
                                                            <Link
                                                                to="/my-account"
                                                                onClick={closeMenu}
                                                                className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                            >
                                                                Hesap Bilgileri
                                                            </Link>
                                                            <Link
                                                                to="/my-account?tab=orders"
                                                                onClick={closeMenu}
                                                                className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                            >
                                                                Siparişlerim
                                                            </Link>
                                                            <Link
                                                                to="/my-account?tab=returns"
                                                                onClick={closeMenu}
                                                                className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                            >
                                                                İadelerim
                                                            </Link>
                                                            {user?.app_metadata?.roles?.includes('admin') && (
                                                                <Link
                                                                    to="/admin"
                                                                    onClick={closeMenu}
                                                                    className="block p-3 rounded-lg hover:bg-gray-50 text-gray-700"
                                                                >
                                                                    Yönetici Paneli
                                                                </Link>
                                                            )}
                                                            <button
                                                                onClick={handleLogout}
                                                                className="w-full text-left p-3 rounded-lg hover:bg-red-50 text-red-600"
                                                            >
                                                                Çıkış Yap
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </nav>
                                        </div>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>
        </>
    );
}

// Kategori Menü Öğesi Bileşeni
const CategoryMenuItem = ({ category, onClose }) => {
    const hasChildren = category.children && category.children.length > 0;

    if (!hasChildren) {
        return (
            <Link
                to={`/categories/${category.slug}`}
                onClick={onClose}
                className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 text-gray-700"
            >
                <div className="flex items-center">
                    {category.icon && (
                        <i className={`${category.icon} text-brand-green mr-3`}></i>
                    )}
                    <span>{category.name}</span>
                </div>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            </Link>
        );
    }

    return (
        <Disclosure>
            {({ open }) => (
                <>
                    <Disclosure.Button className="flex items-center justify-between w-full p-3 rounded-lg hover:bg-gray-50 text-gray-700">
                        <div className="flex items-center">
                            {category.icon && (
                                <i className={`${category.icon} text-brand-green mr-3`}></i>
                            )}
                            <span>{category.name}</span>
                        </div>
                        <ChevronDownIcon 
                            className={`h-4 w-4 text-gray-400 transform transition-transform ${
                                open ? 'rotate-180' : ''
                            }`} 
                        />
                    </Disclosure.Button>
                    
                    <Transition
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0"
                    >
                        <Disclosure.Panel className="ml-6 mt-2 space-y-1">
                            {/* Ana kategori linki */}
                            <Link
                                to={`/categories/${category.slug}`}
                                onClick={onClose}
                                className="block p-2 rounded-lg hover:bg-gray-50 text-sm text-gray-600 font-medium"
                            >
                                Tümünü Gör
                            </Link>
                            
                            {/* Alt kategoriler */}
                            {category.children.map((child) => (
                                <Link
                                    key={child.id}
                                    to={`/categories/${child.slug}`}
                                    onClick={onClose}
                                    className="block p-2 rounded-lg hover:bg-gray-50 text-sm text-gray-600"
                                >
                                    {child.name}
                                </Link>
                            ))}
                        </Disclosure.Panel>
                    </Transition>
                </>
            )}
        </Disclosure>
    );
};

export default MobileMenu;